// selectors
import * as accessoriesSelectors from '@makemydeal/dr-dash-store/selectors/accessoriesSelectors';
import * as compositeSelectors from '@makemydeal/dr-dash-store/selectors/compositeSelectors';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import * as vehicleProtectionSelectors from '@makemydeal/dr-dash-store/selectors/vehicleProtectionSelectors';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { createDealRootSelector } from '../../offerCompareColumn/hooks/createDealRootSelector';

// types
import type { DealHistory } from '@makemydeal/dr-dash-types';
import type { IDealerFee as DealerFee } from '@makemydeal/dr-platform-shared';

// consts/enums
import * as constants from '../../constants';

// utils
import { formatDollarsAndCents } from '@makemydeal/dr-common-utils';
import * as vehicleProtectionUtils from '@makemydeal/dr-dash-store/utils/vehicleProtectionUtils';
import {
    assignFallbackLabels,
    assignVppFallbackLabels,
    convertAccessoryToLineItem,
    convertDealerFeeToLineItem,
    convertGovernmentFeeToLineItem,
    convertLenderFeeToLineItem,
    convertVppProductToLineItem,
    getLabelStyles
} from '../utils/transform';

// components
import { Grid } from '@interstate/components/Grid';
import DealSummaryItem from '../common/DealSummaryItem';
import LineItemWithChildren from '../common/LineItemWithChildren';

// hooks
import { useMemo } from 'react';
import {
    labelStyles as defaultLabelStyles,
    secondaryValueStyles,
    valueStyles as defaultValueStyles,
    detailedViewLabelStyles,
    detailedViewValueStyles,
    detailedViewGridProps,
    defaultGridProps
} from '../utils/styles';

import { useDealSummary } from '../../common/DealSummaryProvider';
import { useMediaQuery } from 'react-responsive';
import { BreakPoint } from '@makemydeal/dr-activities-common';
import { useTrackedSelector } from '../../telemetry/useTrackedSelector';

const expandibleAreaStyles = {
    pr: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
};

const expandibleButtonStyles = {
    py: 1,
    px: 0
};

export type AddOnsProps = {
    deal?: DealHistory;
    hasDelta?: boolean;
};

const Addons = ({ deal, hasDelta = false }: AddOnsProps) => {
    const useDealRootSelector = createDealRootSelector(deal, 'Addons');
    const isEnhancedDealDetailsEnabled = useTrackedSelector(featureToggleSelectors.getEnhancedDealDetails, {
        component: 'Addons',
        field: 'enhancedDealDetails'
    });
    const isLargeSize = useMediaQuery({ query: `(min-width: ${BreakPoint.MEDIUM})` });
    const { showDetailedView } = useDealSummary();

    // total add-ons
    const addons = useDealRootSelector(compositeSelectors.getTotalAddons, 'totalAddons');

    // accessories
    const accessoriesTotal = useDealRootSelector(accessoriesSelectors.getAccessoriesTotalAmount, 'accessoriesTotalAmount');
    const rawAccessories = useDealRootSelector(accessoriesSelectors.getAccessoriesList, 'accessoriesList');
    const itemizedAccessories = useMemo(() => {
        const source = assignFallbackLabels(
            rawAccessories,
            (acc) => acc.name,
            (acc, label) => ({ ...acc, name: label }),
            constants.ACCESSORY_FALLBACK_LABEL
        );
        return source.map(convertAccessoryToLineItem);
    }, [rawAccessories]);

    // protection products
    const protectionProductsTotal = useDealRootSelector(
        (state) => vehicleProtectionSelectors.getVppSummary(state).totalProductPrice,
        'vppSummary'
    );
    const vppProducts = useDealRootSelector(vehicleProtectionSelectors.getVppProducts, 'vppProducts');
    const protectionProductsItems = useMemo(() => {
        const filtered = vppProducts.filter(vehicleProtectionUtils.isVppProductSelected);
        const source = assignVppFallbackLabels(filtered);
        return source.map(convertVppProductToLineItem);
    }, [vppProducts]);

    // dealer fees
    const dealerFeesItems: DealerFee[] = useDealRootSelector(offerSelectors.getDealerFees, 'dealerFees');
    const dealerFeesTotal = useDealRootSelector(offerSelectors.getDealerFeeTotal, 'dealerFeeTotal');
    const dealerFeeItems = useMemo(() => {
        const source = assignFallbackLabels(
            dealerFeesItems,
            (fee) => fee.dealerFeeName,
            (fee, label) => ({ ...fee, dealerFeeName: label }),
            constants.FEE_FALLBACK_LABEL
        );
        return source.filter((fee) => fee.dealerFeeAmount > 0).map(convertDealerFeeToLineItem);
    }, [dealerFeesItems]);

    // government fees
    const governmentFeeTotal = useDealRootSelector(offerSelectors.getDmvFeeTotal, 'dmvFeeTotal');
    const dmvFeeList = useDealRootSelector(offerSelectors.getDmvFeeList, 'dmvFeeList');
    const govFeeItems = useMemo(() => {
        const source = assignFallbackLabels(
            dmvFeeList,
            (fee) => fee.name,
            (fee, label) => ({ ...fee, name: label }),
            constants.FEE_FALLBACK_LABEL
        );
        return source.filter((fee) => fee.amount > 0).map(convertGovernmentFeeToLineItem);
    }, [dmvFeeList]);

    // lender fees
    const lenderFeesTotal = useDealRootSelector(compositeSelectors.getLenderFeesTotal, 'lenderFeesTotal');
    const countedLenderFees = useDealRootSelector(compositeSelectors.getCountedLenderFees, 'itemizedLenderFees');
    const lenderFeeItems = useMemo(() => {
        const source = assignFallbackLabels(
            countedLenderFees,
            (fee) => fee.dealerFeeName,
            (fee, label) => ({ ...fee, dealerFeeName: label }),
            constants.FEE_FALLBACK_LABEL
        );
        return source.filter((fee) => fee.dealerFeeAmount > 0).map(convertLenderFeeToLineItem);
    }, [countedLenderFees]);

    //FeeItems before EnhancedDealDetails
    const FeesItems = [...dealerFeesItems, ...countedLenderFees];
    const FeeItems = useMemo(
        () =>
            FeesItems && FeesItems.length > 0
                ? FeesItems.filter((fee) => fee.dealerFeeAmount > 0).map(convertLenderFeeToLineItem)
                : [],
        [FeesItems]
    );
    const FeesItemsTotal = dealerFeesTotal + lenderFeesTotal;

    const labelStyles = showDetailedView ? detailedViewLabelStyles : defaultLabelStyles;
    const valueStyles = showDetailedView ? detailedViewValueStyles : defaultValueStyles;
    const gridProps = showDetailedView && isLargeSize ? detailedViewGridProps : defaultGridProps;

    return (
        <DealSummaryItem
            labelStyles={isEnhancedDealDetailsEnabled && !showDetailedView ? labelStyles : {}}
            valueStyles={isEnhancedDealDetailsEnabled || showDetailedView ? valueStyles : {}}
            label={constants.TOTAL_ADD_ONS}
            value={formatDollarsAndCents(addons)}
            hasDelta={hasDelta}
        >
            <Grid container sx={gridProps}>
                {isEnhancedDealDetailsEnabled ? (
                    <>
                        <DealSummaryItem
                            valueStyles={secondaryValueStyles}
                            labelStyles={getLabelStyles(showDetailedView, dealerFeeItems.length > 0)}
                            expandibleButtonStyles={expandibleButtonStyles}
                            expandibleAreaStyles={expandibleAreaStyles}
                            label={constants.DEALER_FEES}
                            value={formatDollarsAndCents(dealerFeesTotal)}
                        >
                            {dealerFeeItems &&
                                dealerFeeItems.length > 0 &&
                                dealerFeeItems.map((fee) => (
                                    <LineItemWithChildren key={fee.label} label={fee.label} value={fee.value} />
                                ))}
                        </DealSummaryItem>
                        <DealSummaryItem
                            valueStyles={secondaryValueStyles}
                            labelStyles={getLabelStyles(showDetailedView, govFeeItems.length > 0)}
                            expandibleButtonStyles={expandibleButtonStyles}
                            expandibleAreaStyles={expandibleAreaStyles}
                            label={constants.GOVERNMENT_FEES}
                            value={formatDollarsAndCents(governmentFeeTotal)}
                        >
                            {!!govFeeItems.length &&
                                govFeeItems.map((fee) => (
                                    <LineItemWithChildren key={fee.label} label={fee.label} value={fee.value} />
                                ))}
                        </DealSummaryItem>
                        <DealSummaryItem
                            valueStyles={secondaryValueStyles}
                            labelStyles={getLabelStyles(showDetailedView, lenderFeeItems.length > 0)}
                            expandibleButtonStyles={expandibleButtonStyles}
                            expandibleAreaStyles={expandibleAreaStyles}
                            label={constants.LENDER_FEES}
                            value={formatDollarsAndCents(lenderFeesTotal)}
                        >
                            {!!lenderFeeItems.length &&
                                lenderFeeItems.map((fee) => (
                                    <LineItemWithChildren key={fee.label} label={fee.label} value={fee.value} />
                                ))}
                        </DealSummaryItem>

                        <DealSummaryItem
                            valueStyles={secondaryValueStyles}
                            labelStyles={getLabelStyles(showDetailedView, protectionProductsItems.length > 0)}
                            expandibleButtonStyles={expandibleButtonStyles}
                            expandibleAreaStyles={expandibleAreaStyles}
                            label={constants.PROTECTION_PRODUCTS}
                            value={formatDollarsAndCents(protectionProductsTotal)}
                        >
                            {!!protectionProductsItems.length &&
                                protectionProductsItems.map((product) => (
                                    <LineItemWithChildren key={product.label} label={product.label} value={product.value} />
                                ))}
                        </DealSummaryItem>

                        <DealSummaryItem
                            valueStyles={secondaryValueStyles}
                            labelStyles={getLabelStyles(showDetailedView, itemizedAccessories.length > 0)}
                            expandibleButtonStyles={expandibleButtonStyles}
                            expandibleAreaStyles={expandibleAreaStyles}
                            label={constants.ACCESSORIES}
                            value={formatDollarsAndCents(accessoriesTotal)}
                        >
                            {!!itemizedAccessories.length &&
                                itemizedAccessories.map((accessory) => (
                                    <LineItemWithChildren key={accessory.label} label={accessory.label} value={accessory.value} />
                                ))}
                        </DealSummaryItem>
                    </>
                ) : (
                    <>
                        <LineItemWithChildren
                            label={constants.ACCESSORIES}
                            value={formatDollarsAndCents(accessoriesTotal)}
                            childItems={itemizedAccessories}
                        />
                        <LineItemWithChildren
                            label={constants.PROTECTION_PRODUCTS}
                            value={formatDollarsAndCents(protectionProductsTotal)}
                            childItems={protectionProductsItems}
                        />
                        <LineItemWithChildren
                            label={constants.FEES}
                            value={formatDollarsAndCents(FeesItemsTotal)}
                            childItems={FeeItems}
                        />
                        <LineItemWithChildren
                            label={constants.GOV_FEES}
                            value={formatDollarsAndCents(governmentFeeTotal)}
                            childItems={govFeeItems}
                        />
                    </>
                )}
            </Grid>
        </DealSummaryItem>
    );
};

export default Addons;
